/**
 * Commit Timeline Wrapper
 *
 * Displays commits in the NLE-style timeline.
 * Diffs are only fetched when user explicitly selects commits to compare.
 */

import { useMemo } from 'react'
import { TimelineVisualization } from './TimelineVisualization'
import type { Commit } from './TimelineVisualization'
import type { CommitResponse } from '@/lib/api/commits'

interface CommitTimelineProps {
  commits: CommitResponse[]
  selectedCommitId: string | null
  onSelectCommit: (commitId: string) => void
  className?: string
}

/**
 * Transform API commits to timeline format
 * NOTE: Diffs are NOT fetched here - only when user selects commits to compare
 */
function transformCommits(commits: CommitResponse[]): Commit[] {
  return commits.map((commit) => {
    // Extract timeline name from commit_metadata
    const timelineName = commit.commit_metadata?.timeline_name

    return {
      id: commit.id,
      message: commit.message,
      timestamp: new Date(commit.created_at),
      author: commit.author_name || 'Unknown',
      // Show neutral state - diffs only loaded on-demand
      changeCount: {
        added: 0,
        removed: 0,
        trimmed: 0,
        moved: 0,
      },
      // Pass timeline name as a JSON string for compatibility with TimelineVisualization
      otio_timeline: timelineName ? JSON.stringify({ name: timelineName }) : undefined
    }
  })
}

export function CommitTimeline({
  commits,
  selectedCommitId,
  onSelectCommit,
  className
}: CommitTimelineProps) {
  // Transform commits without fetching diffs
  // Diffs are only fetched when user selects commits to compare
  const timelineCommits = useMemo(
    () => transformCommits(commits),
    [commits]
  )

  return (
    <TimelineVisualization
      commits={timelineCommits}
      selectedCommitId={selectedCommitId}
      onSelectCommit={onSelectCommit}
      className={className}
    />
  )
}
